import { Button } from "@/v5/components/ui/button";
import { Input } from "@/v5/components/ui/input";
import {
  getWhatsAppConsent,
  getWhatsAppTemplates,
  setWhatsAppConsent,
  type WhatsAppTemplate,
} from "@/services/whatsappMessaging.service";
import { Loader2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";

/**
 * What the composer offers once the patient's reply window has closed.
 *
 * WhatsApp carries a freely-written message from a practice only for a day
 * after the patient writes to them. After that, the only thing that will be
 * delivered is one of the messages WhatsApp has approved in advance — which is
 * why staff pick from a list here rather than typing.
 *
 * And an approved message STARTS a conversation the patient did not ask for, so
 * it may only go to someone who agreed to be contacted on WhatsApp. That is the
 * consent step below. The server enforces both independently; this exists so
 * staff find out before they compose, not after they press send.
 *
 * Nothing here blocks the text-message alternative, which always works.
 */
/** A stored date, shown the way staff read dates rather than as a timestamp. */
function formatConsentDate(raw: string): string {
  const parsed = new Date(raw.replace(' ', 'T'));
  if (Number.isNaN(parsed.getTime())) return raw;
  return parsed.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

interface Props {
  patientId?: string;
  /** Send the chosen approved message. Resolves when it has gone. */
  onSend: (contentSid: string, variables: Record<string, string>) => Promise<void>;
}

export function WhatsAppApprovedMessage({ patientId, onSend }: Props) {
  const queryClient = useQueryClient();
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [awaitingApproval, setAwaitingApproval] = useState(false);
  /** null = we could not reach a verdict, which is not the same as "none". */
  const [reachable, setReachable] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);

  const [optedIn, setOptedIn] = useState<boolean | null>(null);
  const [consentSource, setConsentSource] = useState<string | null>(null);
  const [consentWhen, setConsentWhen] = useState<string | null>(null);
  const [consentBy, setConsentBy] = useState<string | null>(null);
  /** The affirmation itself. Deliberately starts unticked every time. */
  const [affirmed, setAffirmed] = useState(false);
  const [recordingConsent, setRecordingConsent] = useState(false);

  const [chosenId, setChosenId] = useState("");
  const [values, setValues] = useState<Record<string, string>>({});
  const [sending, setSending] = useState(false);

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    Promise.all([
      getWhatsAppTemplates().catch(() => null),
      patientId ? getWhatsAppConsent(patientId).catch(() => null) : Promise.resolve(null),
    ]).then(([list, consent]) => {
      if (cancelled) return;
      if (list) {
        setTemplates(list.templates);
        setAwaitingApproval(list.awaitingApproval);
        setReachable(list.ok);
      } else {
        setReachable(false);
      }
      setOptedIn(consent ? consent.optedIn : null);
      setConsentSource(consent?.source ?? null);
      setConsentWhen(consent?.recordedAt ?? null);
      setConsentBy(consent?.recordedBy ?? null);
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [patientId]);

  const chosen = useMemo(
    () => templates.find((t) => t.id === chosenId) ?? null,
    [templates, chosenId],
  );

  /** Every placeholder filled — an empty one would reach the patient blank. */
  const ready = useMemo(() => {
    if (!chosen) return false;
    return chosen.variables.every((key) => (values[key] ?? "").trim() !== "");
  }, [chosen, values]);

  const preview = useMemo(() => {
    if (!chosen) return "";
    return chosen.variables.reduce(
      (text, key) => text.split(`{{${key}}}`).join(values[key] ?? `{{${key}}}`),
      chosen.body,
    );
  }, [chosen, values]);

  const handleRecordConsent = useCallback(async () => {
    if (!patientId || !affirmed) return;
    setRecordingConsent(true);
    try {
      const result = await setWhatsAppConsent(patientId, true, true);
      setOptedIn(result.optedIn);
      setConsentSource(result.source);
      setConsentWhen(result.recordedAt);
      setConsentBy(result.recordedBy);
      // The patient chart reads the same record through React Query with a
      // 60s staleTime, so without this it keeps showing "not recorded" for a
      // minute after someone records it here.
      queryClient.invalidateQueries({ queryKey: ["whatsapp-consent", patientId] });
    } finally {
      setRecordingConsent(false);
    }
  }, [patientId, affirmed, queryClient]);

  const handleSend = useCallback(async () => {
    if (!chosen || !ready || sending) return;
    setSending(true);
    try {
      await onSend(chosen.id, values);
      setChosenId("");
      setValues({});
    } finally {
      setSending(false);
    }
  }, [chosen, ready, sending, onSend, values]);

  if (loading) {
    return (
      <div className="mb-2 px-3 py-2 rounded-lg bg-muted/60 border border-border flex items-center gap-2">
        <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
        <span className="text-xs text-muted-foreground">Checking what you can send…</span>
      </div>
    );
  }

  return (
    <div className="mb-2 px-3 py-2.5 rounded-lg bg-amber-50 border border-amber-200 space-y-2">
      <div>
        <p className="text-xs font-semibold text-amber-900">
          You can't write freely to this patient right now.
        </p>
        <p className="text-xs text-amber-800 leading-snug mt-0.5">
          WhatsApp only allows that for a day after the patient messages you. Until they
          do, you can send one of your practice's ready-made messages below — or send a
          text message, which will go through now.
        </p>
      </div>

      {/* Could not reach a verdict. Never shown as "you have none": staff would
          go off re-creating messages the practice already has. */}
      {reachable === false && (
        <p className="text-xs text-amber-800 leading-snug">
          We couldn't load your ready-made messages just now. Try again in a moment, or
          send this as a text message.
        </p>
      )}

      {reachable === true && templates.length === 0 && (
        <p className="text-xs text-amber-800 leading-snug">
          {awaitingApproval
            ? "Your practice's ready-made messages haven't been approved yet. Send this as a text message in the meantime."
            : "Your practice has no ready-made messages set up yet. An office administrator can add them. Send this as a text message in the meantime."}
        </p>
      )}

      {/* Consent. Asked before composing rather than after pressing send.

          The checkbox is the attestation, and it is a separate deliberate act
          from the button on purpose — a single click that both asserts and
          submits is indistinguishable from an accident, and this ends up on the
          patient's record as a named person's word. */}
      {reachable === true && templates.length > 0 && optedIn === false && (
        <div className="space-y-1.5">
          <p className="text-xs text-amber-800 leading-snug">
            You can only message this patient here if they've said they're happy to be
            contacted on WhatsApp.
          </p>
          <label className="flex items-start gap-2 text-xs text-amber-900 cursor-pointer">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={affirmed}
              onChange={(e) => setAffirmed(e.target.checked)}
            />
            <span>I asked the patient and they agreed to be contacted on WhatsApp.</span>
          </label>
          <p className="text-xs text-amber-800 leading-snug">
            This is saved against the patient's record with your name and today's date.
          </p>
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs"
            disabled={recordingConsent || !patientId || !affirmed}
            onClick={handleRecordConsent}
          >
            {recordingConsent && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
            Save this
          </Button>
        </div>
      )}

      {/* What is on file, once there is something. A bare "they agreed" is a
          claim nobody can check; naming who said it and when is the whole
          difference between a checkbox and a record. */}
      {reachable === true && templates.length > 0 && optedIn === true && consentSource && (
        <p className="text-xs text-amber-800 leading-snug">
          {consentSource}
          {consentBy ? ` — ${consentBy}` : ''}
          {consentWhen ? `, ${formatConsentDate(consentWhen)}` : ''}
        </p>
      )}

      {reachable === true && templates.length > 0 && optedIn && (
        <div className="space-y-2">
          <select
            className="w-full h-8 text-xs rounded-md border border-amber-300 bg-white px-2"
            value={chosenId}
            onChange={(e) => {
              setChosenId(e.target.value);
              setValues({});
            }}
            aria-label="Choose a ready-made message"
          >
            <option value="">Choose a ready-made message…</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>

          {chosen && chosen.variables.length > 0 && (
            <div className="space-y-1.5">
              {chosen.variables.map((key, index) => (
                <Input
                  key={key}
                  className="h-8 text-xs bg-white"
                  placeholder={`Fill in blank ${index + 1}`}
                  aria-label={`Fill in blank ${index + 1}`}
                  value={values[key] ?? ""}
                  onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))}
                />
              ))}
            </div>
          )}

          {chosen && (
            <p className="text-xs text-amber-900 bg-white/70 rounded p-2 leading-snug whitespace-pre-wrap">
              {preview}
            </p>
          )}

          <Button
            size="sm"
            className="h-7 text-xs"
            disabled={!ready || sending}
            onClick={handleSend}
          >
            {sending && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
            Send this message
          </Button>
        </div>
      )}
    </div>
  );
}
